const emptyParagraphMarker = '<br />';
const emptyParagraphTagPattern = /^<br\s*\/?>$/i;

export function sanitizeMarkdown(markdown: string | null | undefined): string {
  const normalizedMarkdown = (markdown ?? '')
    .replace(/\r\n?/g, '\n')
    .replace(/&lt;br\s*\/?(?:&gt;|>)/gi, emptyParagraphMarker)
    .replace(/<br\s*\/?>/gi, emptyParagraphMarker);

  return repairSerializedMarkdownTables(normalizedMarkdown)
    .replace(/<\/?[a-zA-Z][^>\n]*>/g, escapeHtmlTag)
    .replace(/\]\(\s*(?:javascript|data|vbscript):[^)]*\)/gi, '](#)');
}

export function getChangedMarkdown(markdown: string, previousMarkdown: string): string | null {
  const sanitizedMarkdown = sanitizeMarkdown(markdown);
  const sanitizedPreviousMarkdown = sanitizeMarkdown(previousMarkdown);
  return trimTrailingLineFeeds(sanitizedMarkdown) === trimTrailingLineFeeds(sanitizedPreviousMarkdown)
    ? null
    : sanitizedMarkdown;
}

function trimTrailingLineFeeds(markdown: string): string {
  return markdown.replace(/\n+$/, '');
}

function escapeHtmlTag(tag: string): string {
  if (emptyParagraphTagPattern.test(tag)) {
    return emptyParagraphMarker;
  }

  return `&lt;${tag.slice(1)}`;
}

function unescapeLeadingTablePipe(line: string): string {
  return line.replace(/^(\s*)\\\|/, '$1|');
}

function isPipeTableCandidateLine(line: string): boolean {
  return unescapeLeadingTablePipe(line).trim().startsWith('|');
}

function readTableCells(line: string): string[] {
  let content = unescapeLeadingTablePipe(line).trim();
  if (content.startsWith('|')) {
    content = content.slice(1);
  }

  if (content.endsWith('|')) {
    content = content.slice(0, -1);
  }

  return splitTableCells(content);
}

function splitTableCells(content: string): string[] {
  const cells: string[] = [];
  let cell = '';
  let escaped = false;

  for (const char of content) {
    if (char === '|' && !escaped) {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += char;
    }

    if (escaped) {
      escaped = false;
    } else {
      escaped = char === '\\';
    }
  }

  cells.push(cell.trim());
  return cells;
}

function isDelimiterCell(cell: string): boolean {
  return /^:?-{1,}:?$/.test(cell.trim());
}

function isDelimiterRow(line: string): boolean {
  const cells = readTableCells(line);
  return cells.length > 0 && cells.every(isDelimiterCell);
}

function formatTableRow(cells: string[]): string {
  return `| ${cells.map(cell => cell.trim()).join(' | ')} |`;
}

function formatDelimiterRow(cells: string[], columns: number): string {
  const normalizedCells = cells.slice(0, columns).map(cell => {
    const trimmed = cell.trim();
    return `${trimmed.startsWith(':') ? ':' : ''}---${trimmed.endsWith(':') ? ':' : ''}`;
  });

  while (normalizedCells.length < columns) {
    normalizedCells.push('---');
  }

  return `| ${normalizedCells.join(' | ')} |`;
}

function padTableCells(cells: string[], columns: number): string[] {
  const normalizedCells = cells.slice(0, columns);
  while (normalizedCells.length < columns) {
    normalizedCells.push('');
  }

  return normalizedCells;
}

function isPipeOnlyLine(line: string): boolean {
  return /^\|+$/.test(unescapeLeadingTablePipe(line).trim());
}

function collectTableRows(lines: string[], columns: number): string[][] {
  const rows: string[][] = [];
  let cells: string[] = [];
  let leadingPipeOnlyCount = 0;

  const flush = () => {
    if (leadingPipeOnlyCount > 0 && cells.length === 0) {
      const emptyCells = Math.max(0, leadingPipeOnlyCount - 1);
      if (emptyCells > 0) {
        rows.push(padTableCells(Array(emptyCells).fill(''), columns));
      }
      leadingPipeOnlyCount = 0;
      return;
    }

    if (cells.length > 0) {
      rows.push(padTableCells(cells, columns));
      cells = [];
    }
    leadingPipeOnlyCount = 0;
  };

  for (const line of lines) {
    const normalizedLine = unescapeLeadingTablePipe(line);
    if (isPipeOnlyLine(normalizedLine)) {
      if (cells.length === 0) {
        leadingPipeOnlyCount += normalizedLine.trim().length;
      } else {
        cells.push('');
        if (cells.length >= columns) {
          flush();
        }
      }
      continue;
    }

    if (leadingPipeOnlyCount > 0 || cells.length > 0) {
      flush();
    }

    cells = readTableCells(normalizedLine);
    if (cells.length >= columns) {
      flush();
    }
  }

  flush();
  return rows;
}

function repairPipeTableBlock(lines: string[]): string[] {
  const normalizedLines = lines.map(unescapeLeadingTablePipe);
  const delimiterIndex = normalizedLines.findIndex(isDelimiterRow);
  if (delimiterIndex <= 0) {
    return lines;
  }

  const delimiterCells = readTableCells(normalizedLines[delimiterIndex]);
  const columns = delimiterCells.length;
  const headerRows = collectTableRows(normalizedLines.slice(0, delimiterIndex), columns);
  if (headerRows.length === 0) {
    return normalizedLines;
  }

  const bodyRows = collectTableRows(normalizedLines.slice(delimiterIndex + 1), columns);
  if (bodyRows.length === 0) {
    bodyRows.push(Array(columns).fill(''));
  }

  const headerRow = headerRows[headerRows.length - 1];
  return [
    formatTableRow(headerRow),
    formatDelimiterRow(delimiterCells, columns),
    ...bodyRows.map(formatTableRow)
  ];
}

function getFenceMarker(line: string): string | null {
  const match = line.match(/^\s*(`{3,}|~{3,})/);
  return match?.[1] ?? null;
}

function closesFence(marker: string, fenceMarker: string): boolean {
  return marker[0] === fenceMarker[0] && marker.length >= fenceMarker.length;
}

function repairSerializedMarkdownTables(markdown: string): string {
  const lines = markdown.split('\n');
  const result: string[] = [];
  let block: string[] = [];
  let fenceMarker: string | null = null;

  const flushBlock = () => {
    if (block.length > 0) {
      result.push(...repairPipeTableBlock(block));
      block = [];
    }
  };

  for (const line of lines) {
    const marker = getFenceMarker(line);
    if (marker != null) {
      flushBlock();
      result.push(line);
      if (fenceMarker == null) {
        fenceMarker = marker;
      } else if (closesFence(marker, fenceMarker)) {
        fenceMarker = null;
      }
      continue;
    }

    if (fenceMarker == null && isPipeTableCandidateLine(line)) {
      block.push(line);
      continue;
    }

    flushBlock();
    result.push(line);
  }

  flushBlock();
  return result.join('\n');
}
